import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { popupMedia, popupMetrics } from './shell/tokens.js';
import { tokens } from './tokens.js';
/**
 * The pop-up rules, as the one place a dialog takes its shape from (Figma `1038:2` cards 11 and 12, round 2's 15 and 18; #501, #502).
 * The numbers live in `shell/tokens.ts` (`popupMetrics`, `popupMedia`); this turns them into class names.
 *
 * Under `popupMetrics.stackBelowPx` a dialog fills the width with a 16px margin and its buttons stack with the action on
 * top. Up to the shell's phone width every button and field is a touch target (44 high), and that is NOT done here: `ThemeProvider`
 * already applies it to every Fluent control at `shellMedia.phone`, so a dialog gets it from there. The DOM order of a dialog's buttons stays
 * Cancel then action, which is how it reads on a wide screen; stacking reverses it so the action is the first thing on a phone.
 */
export const popupSizes = { small: popupMetrics.small, medium: popupMetrics.medium, large: popupMetrics.large };
const margin = `calc(100vw - 2 * ${tokens.spacingHorizontalL})`;
const useShape = makeStyles({
    small: { width: popupSizes.small, maxWidth: popupSizes.small, [popupMedia.stack]: { width: margin, maxWidth: margin } },
    medium: { width: popupSizes.medium, maxWidth: popupSizes.medium, [popupMedia.stack]: { width: margin, maxWidth: margin } },
    large: { width: '100%', maxWidth: popupSizes.large, [popupMedia.stack]: { width: margin, maxWidth: margin } },
    /** Never taller than the window allows; the surface is a column so the middle (`content`) is what scrolls. */
    height: { maxHeight: popupMetrics.maxHeight, display: 'flex', flexDirection: 'column' },
    body: { minHeight: 0, maxHeight: popupMetrics.maxHeight, overflow: 'hidden', width: '100%' },
    content: { minHeight: 0, overflowY: 'auto' },
    // Fluent's DialogBody is a grid (title row 1, content row 2, buttons row 3). A child with no grid place goes into the first free cell, which
    // was row 3 beside the buttons, so the pinned part takes the whole of row 3 and the buttons move down to row 4 when there is one.
    pinned: { flexShrink: 0, gridRowStart: 3, gridRowEnd: 3, gridColumnStart: 1, gridColumnEnd: -1 },
    belowPinned: { gridRowStart: 4, gridRowEnd: 'auto' },
    actions: { [popupMedia.stack]: { flexDirection: 'column-reverse', alignItems: 'stretch' } },
    // A dialog's button label is one line: it takes the width it needs rather than wrapping inside a button that is too narrow.
    button: { whiteSpace: 'nowrap', flexShrink: 0, [popupMedia.stack]: { width: '100%' } },
});
/** Class names for a dialog's surface (by size), its DialogActions, and each button in it. */
export function useDialogShape(size) {
    const shape = useShape();
    return { surface: shape[size], actions: shape.actions, button: shape.button };
}
/** What `PopupDialog` needs beyond `useDialogShape`. */
export function usePopupShape(size) {
    const shape = useShape();
    return {
        surface: mergeClasses(shape[size], shape.height),
        body: shape.body,
        content: shape.content,
        pinned: shape.pinned,
        actions: shape.actions,
        /** Added to `actions` when a pinned part is shown, so the buttons sit in the row under it. */
        belowPinned: shape.belowPinned,
        button: shape.button,
    };
}
//# sourceMappingURL=popups.js.map